import axios from 'axios'
import { env } from '@/config/env'
import { getUrlParam } from '@/utils/urlUtils'

// 获取url的appType存储起来
const appType = getUrlParam('appType')

// 全局路由实例（将在main.js中设置）
let globalRouter = null

// 设置全局路由实例的方法
export const setGlobalRouter = (router) => {
  globalRouter = router
}

// 状态码定义
export const STATUS_CODES = {
  // SUCCESS: 200,
  // CREATED: 201,
  // NO_CONTENT: 204,
  // BAD_REQUEST: 400,
  // UNAUTHORIZED: 401,
  // FORBIDDEN: 403,
  // NOT_FOUND: 404,
  // METHOD_NOT_ALLOWED: 405,
  // CONFLICT: 409,
  // UNPROCESSABLE_ENTITY: 422,
  // TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
}

// 错误消息映射
export const ERROR_MESSAGES = {
  // [STATUS_CODES.BAD_REQUEST]: '请求参数错误',
  // [STATUS_CODES.UNAUTHORIZED]: '未授权，请重新登录',
  // [STATUS_CODES.FORBIDDEN]: '访问被拒绝',
  // [STATUS_CODES.NOT_FOUND]: '请求的资源不存在',
  // [STATUS_CODES.METHOD_NOT_ALLOWED]: '请求方法不允许',
  // [STATUS_CODES.CONFLICT]: '数据冲突',
  // [STATUS_CODES.UNPROCESSABLE_ENTITY]: '数据验证失败',
  // [STATUS_CODES.TOO_MANY_REQUESTS]: '请求过于频繁，请稍后再试',
  [STATUS_CODES.INTERNAL_SERVER_ERROR]: '服务器内部错误',
  [STATUS_CODES.BAD_GATEWAY]: '网关错误',
  [STATUS_CODES.SERVICE_UNAVAILABLE]: '服务暂时不可用',
  [STATUS_CODES.GATEWAY_TIMEOUT]: '网关超时',
  NETWORK_ERROR: '网络连接错误',
  TIMEOUT_ERROR: '请求超时',
  UNKNOWN_ERROR: '未知错误'
}

// 获取当前语言设置
const getCurrentLocale = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('locale') || 'zh-CN'
  }
  return 'zh-CN'
}


// 创建axios实例
const http = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'App-Type': appType,
  },
  // 生产环境跨域配置
  withCredentials: env.isProduction, // 如果需要携带cookies
})

// 请求拦截器
http.interceptors.request.use(
  (config) => {
    // 动态设置当前语言到请求头
    config.headers['Content-Language'] = getCurrentLocale()
    // 添加token到请求头
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 添加时间戳防止缓存
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }

    if (env.DEBUG) {
      console.log('Request:', config)
    }

    return config
  },
  (error) => {
    if (env.DEBUG) {
      console.error('Request Error:', error)
    }
    return Promise.reject(error)
  }
)

// 响应拦截器
http.interceptors.response.use(
  (response) => {
    if (env.DEBUG) {
      console.log('Response:', response)
    }

    // 检查业务状态码
    const { data } = response
    if (data && typeof data.code !== 'undefined') {
      if (data.code === 0 || data.code === 200) {
        return data
      } else if (data.code === 404) {
        // return Promise.reject(data)

        console.log('跳转404页面')
        // 特殊状态码404，跳转到404页面
        if (typeof window !== 'undefined') {
          if (globalRouter) {
            globalRouter.push(`/404?appType=${appType}`)
          } else {
            // 如果全局路由不可用，使用window.location
            window.location.href = `/404?appType=${appType}`
          }
        }
        // 返回rejected promise
        const error = new Error(data.message || data.msg || ERROR_MESSAGES.UNKNOWN_ERROR)
        error.code = data.code
        error.data = data
        return Promise.reject(error)
      } else {
        // 业务错误
        const error = new Error(data.message || data.msg || ERROR_MESSAGES.UNKNOWN_ERROR)
        error.code = data.code
        error.data = data
        return Promise.reject(error)
      }
    }

    return response.data
  },
  (error) => {
    if (env.DEBUG) {
      console.error('Response Error:', error)
    }

    let errorMessage = ERROR_MESSAGES.UNKNOWN_ERROR
    let errorCode = null

    if (error.response) {
      // 服务器响应了错误状态码
      const { status, data } = error.response
      errorCode = status

      if (data && data.message) {
        errorMessage = data.message
      } else if (data && data.msg) {
        errorMessage = data.msg
      } else {
        errorMessage = ERROR_MESSAGES[status] || ERROR_MESSAGES.UNKNOWN_ERROR
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      errorMessage = ERROR_MESSAGES.NETWORK_ERROR
    } else if (error.code === 'ECONNABORTED') {
      // 请求超时
      errorMessage = ERROR_MESSAGES.TIMEOUT_ERROR
    } else {
      // 其他错误
      errorMessage = error.message || ERROR_MESSAGES.UNKNOWN_ERROR
    }

    // 统一错误处理
    const customError = new Error(errorMessage)
    customError.code = errorCode
    customError.originalError = error

    // 特殊状态码处理
    if (errorCode === STATUS_CODES.UNAUTHORIZED) {
      // 清除token并跳转到登录页
      localStorage.removeItem('token')
      // 可以在这里添加路由跳转逻辑
    }

    return Promise.reject(customError)
  }
)

// 导出HTTP客户端
export default http

// 导出常用的HTTP方法
export const httpClient = {
  get: (url, config) => http.get(url, config),
  post: (url, data, config) => http.post(url, data, config),
  put: (url, data, config) => http.put(url, data, config),
  delete: (url, config) => http.delete(url, config),
  patch: (url, data, config) => http.patch(url, data, config)
}
